import { UserType, B2BChannelType, B2BRegion } from '@tcr-erp/shared';
import api from './api';
import type {
    CustomerAddressItem,
    CustomerAddressImportPreview,
    CustomerAddressImportResult,
    CustomerBankItem,
    CustomerLifecycleState,
    CustomerPurchase,
    TenantCustomer,
} from '@/mocks/customers';
import type { CustomerListQuery } from './queryKeys';

export interface CustomerListResponse {
    data: TenantCustomer[];
    total: number;
}

// TCR-1552 — 1 dòng lịch sử thay đổi. `type` khớp `CustomerLogType` enum ở
// BE (create_customer/update_customer/...); `old_data`/`new_data` là object
// tự do (diff theo field DTO client gửi, xem BE `customer.service.ts`
// update()), FE chỉ liệt kê key/value thô, không cố định hình dạng.
export interface CustomerLogEntryBE {
    _id: string;
    customer_id: string;
    staff_id?: string | null;
    type: string;
    old_data?: Record<string, unknown> | null;
    new_data?: Record<string, unknown> | null;
    created_at: string;
}

export interface CustomerLogsResponse {
    data: CustomerLogEntryBE[];
    pagination: { page: number; limit: number; total: number; total_pages: number };
}

// TCR-1415 — số dư trả trước của khách hàng.
export interface CustomerCreditSummary {
    credit_balance: number;
    total_deposit: number;
    total_spent: number;
}

export type CustomerCreditTxnType = 'deposit' | 'withdraw' | 'purchase' | 'refund' | 'adjust';

export interface CustomerCreditTransactionItem {
    _id: string;
    type: CustomerCreditTxnType;
    amount: number;
    balance_after: number;
    method: 'cash' | 'transfer' | 'card';
    note: string;
    created_at: string;
}

export interface CustomerCreditTransactionsResponse {
    data: CustomerCreditTransactionItem[];
    pagination: { page: number; limit: number; total: number; total_pages: number };
}

export interface TenantCustomerProfileSummary {
    tenant_id: string;
    tenant_customer_id: string;
    customer_id: string;
    partner_type: 'customer' | 'supplier';
    partner_types?: Array<'customer' | 'supplier'>;
    code?: string;
    name?: string;
    tax_code?: string;
    state?: 'active' | 'inactive' | 'trash';
}

export interface TenantCustomerProfileListQuery {
  keyword?: string;
  partner_type?: 'customer' | 'supplier';
  partner_types?: Array<'customer' | 'supplier'>;
  state?: 'active' | 'inactive' | 'trash';
  customer_ids?: string;
  limit?: number;
}

/**
 * TCR — Omnichat Customer Info Sidebar (Task 4).
 *
 * `GET /customers/profiles/:tenantCustomerId` (be-tenant `customer.service.ts::
 * detailByTenantCustomerId` → `mergeTenantCustomer()`) trả về bản merge đầy đủ
 * `Customer` + `TenantCustomer`, nhưng `getProfileByTenantCustomerId()` KHÔNG đi
 * qua `fromCustomerBE` (hàm camelCase-mapping dùng ở các method khác trong file
 * này) — đây là passthrough thô của response snake_case gốc từ BE. Vì vậy field
 * ở type này giữ nguyên snake_case, khác với `TenantCustomer` (camelCase) ở chỗ
 * khác trong cùng file.
 *
 * `credit_limit` là HẠN MỨC công nợ được phép (nullable — null/absent = chưa đặt
 * hạn mức riêng), KHÔNG phải số nợ hiện tại (số nợ hiện tại cần cộng dồn đơn
 * hàng chưa thanh toán — nằm ngoài phạm vi task này, xem task-4-brief.md).
 * `credit_balance` (ở nơi khác của hệ thống) là số dư TRẢ TRƯỚC, ngược nghĩa với
 * công nợ — không nhầm lẫn 2 field này.
 */
export interface TenantCustomerProfileDetail {
    _id?: string;
    tenant_customer_id?: string;
    code?: string;
    name?: string;
    phone?: string;
    address?: string;
    city?: string;
    district?: string;
    city_id?: string;
    ward_id?: string;
    total_spent?: number;
    total_purchases?: number;
    note?: string;
    updated_at?: string;
    credit_limit?: number | null;
    /** CRM sales owner stored on TenantCustomer (snake_case passthrough from GET profile). */
    assigned_staff_id?: string | null;
}

export type CreateCustomerPayload = Omit<
    TenantCustomer,
    '_id' | 'code' | 'totalSpent' | 'loyaltyPoints' | 'loyaltyTierId' | 'createdAt' | 'updatedAt' | 'lastPurchaseAt'
> & { code?: string };

export type UpdateCustomerPayload = Partial<CreateCustomerPayload>;

/** TCR-1050 — đơn hàng rút gọn của một khách, đủ để dựng ô chọn đơn trên phiếu thu. */
export interface CustomerOrderRef {
    order_id: string;
    order_number: string;
    order_date: string;
    store_id?: string;
    state: string;
    payment_state: string;
    total_amount: number;
}

/** Bản thô từ `GET /customers/:id/purchases` — đơn hàng đầy đủ, chỉ đọc vài field. */
interface CustomerOrderRefBE {
    _id?: string;
    order_id?: string;
    order_number?: string;
    order_date?: string;
    created_at?: string;
    store_id?: string;
    state?: string;
    payment_state?: string;
    summary?: { total?: number | { $numberDecimal?: string } };
    total?: number | { $numberDecimal?: string };
}

/** `summary.total` có thể là Decimal128 (`{$numberDecimal}`) với đơn tạo từ POS. */
function readOrderTotal(order: CustomerOrderRefBE): number {
    const raw = order.summary?.total ?? order.total ?? 0;
    if (typeof raw === 'object' && raw !== null) {
        return Number(raw.$numberDecimal ?? 0);
    }
    return Number(raw) || 0;
}

/**
 * Map FE-friendly query keys → BE DTO whitelist (`tcr-erp-be-tenant/.../list-customer.dto.ts`).
 * BE dùng `class-validator` với `forbidNonWhitelisted: true` → request có
 * key lạ sẽ bị reject 400. Các tên BE DTO expect: q / tags / limit (không
 * phải search / tag / pageSize).
 */
function mapListQuery(p: CustomerListQuery): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    if (p.page !== undefined) out.page = p.page;
    if (p.pageSize !== undefined) out.limit = Math.min(p.pageSize, 100);
    if (p.search !== undefined) out.q = p.search;
    if (p.loyaltyTierId !== undefined) out.loyalty_tier_id = p.loyaltyTierId;
    if (p.tag !== undefined) out.tags = p.tag;
    if (p.groupId !== undefined) out.group_id = p.groupId;
    if (p.state !== undefined) out.state = p.state;
    if (p.type !== undefined) out.type = p.type;
    // TCR-2507 — lọc khách CHƯA có NVKD phụ trách: BE dùng query RIÊNG
    // `unassigned_staff=true` và KHÔNG nhận `assigned_staff_id` đi kèm → khi bật
    // ca "chưa phân công" thì bỏ luôn `assigned_staff_id` (tránh gửi cả 2 lên BE).
    if (p.unassignedStaff) {
        out.unassigned_staff = true;
    } else if (p.assignedStaffId !== undefined) {
        out.assigned_staff_id = p.assignedStaffId;
    }
    if (p.partnerType !== undefined) out.partner_type = p.partnerType;
    if (p.totalSpentMin !== undefined) out.min_total_spent = p.totalSpentMin;
    if (p.totalSpentMax !== undefined) out.max_total_spent = p.totalSpentMax;
    if (p.loyaltyPointsMin !== undefined) out.min_loyalty_points = p.loyaltyPointsMin;
    if (p.loyaltyPointsMax !== undefined) out.max_loyalty_points = p.loyaltyPointsMax;
    if (p.lastPurchaseFrom !== undefined) out.last_purchase_from = p.lastPurchaseFrom;
    if (p.lastPurchaseTo !== undefined) out.last_purchase_to = p.lastPurchaseTo;
    if (p.amisSyncStatus !== undefined) out.amis_sync_status = p.amisSyncStatus;
    // TCR-2070: pass-through, KHÔNG map camelCase (ADR-013), giống các field b2b_channel/region khác.
    if (p.b2bChannel !== undefined) out.b2b_channel = p.b2bChannel;
    if (p.region !== undefined) out.region = p.region;
    return out;
}

type CustomerGroupBE =
    | string
    | {
        _id?: string;
        id?: string;
        user_group_id?: string;
        code?: string;
        name?: string;
        group_name?: string;
        group_code?: string;
        color?: string;
    };

/** Customer raw từ BE — snake_case + có thêm field internal (tenant_id, __v). */
interface CustomerBE {
    _id: string;
    tenant_customer_id?: string;
    code: string;
    name: string;
    phone: string;
    email?: string;
    birthday?: string;
    gender?: 'male' | 'female' | 'other';
    id_card?: string;
    idCard?: string;
    id_card_type?: TenantCustomer['idCardType'];
    idCardType?: TenantCustomer['idCardType'];
    id_card_front_url?: string | null;
    id_card_back_url?: string | null;
    address?: string;
    district?: string;
    city?: string;
    city_id?: string;
    ward_id?: string;
    tags?: string[];
    total_spent?: number;
    loyalty_points?: number;
    loyalty_tier_id?: string | null;
    last_purchase_at?: string;
    note?: string;
    created_at?: string;
    updated_at?: string;
    type?: UserType;
    credit_limit?: number | null;
    // TCR-1551: BE trả `null` khi MST đã bị xoá (schema default null).
    tax_code?: string | null;
    tax_email?: string;
    invoice_organization_name?: string;
    invoice_buyer_name?: string;
    invoice_buyer_cccd?: string;
    amis_account_object_code?: string;
    amis_account_object_id?: string;
    invoice_address?: string;
    group_id?: CustomerGroupBE | null;
    group?: CustomerGroupBE | null;
    /** TCR-1042: pass-through, KHÔNG map camelCase (ADR-013). */
    b2b_channel?: B2BChannelType | null;
    /** TCR-1520: pass-through, KHÔNG map camelCase (ADR-013), giống b2b_channel. */
    region?: B2BRegion | null;
    /** TCR-1548: pass-through, KHÔNG map camelCase (ADR-013), giống region. */
    payment_terms_days?: number | null;
    /** TCR-466: thông tin KH B2B / tổ chức. */
    contact_name?: string;
    contact_title?: string;
    contact_phone?: string;
    assigned_staff_id?: string;
    /**
     * TCR-1473: raw `TenantCustomer.assigned_sale_ids` (ObjectId[] → BE trả
     * về dạng chuỗi hex qua JSON). BE `list()`/`detail()` trả về field này
     * (merge nguyên `tenantCustomer.toObject()` — xem
     * `customer.service.ts#mergeTenantCustomer` phía BE). (final fix wave,
     * Fix 7) — `PUT /customers/:id` VÀ `PUT /customers/profiles/:id` giờ đều
     * nhận field này để ghi (gate bằng `CustomerTransferGuard`, xem
     * `AssignSaleModal.tsx`/`updateAssignedSaleIds` ở file này) — field
     * dùng được cho CẢ đọc lẫn ghi.
     */
    assigned_sale_ids?: string[];
    partner_types?: ('customer' | 'supplier')[];
    state?: TenantCustomer['state'];
    /** TCR-1261: BE tính on-demand mỗi lần GET /customers/:id, không phải field tĩnh. */
    health_score?: number;
    /** TCR-1437: lý do khách CHƯA đẩy được lên AMIS — tiếng Việt, hiển thị thẳng, không ghép chữ. */
    amis_push_blocked_reason?: string | null;
    /** TCR-1553: xem comment tương ứng trong `TenantCustomer` (mocks/customers.ts). */
    amis_pushed_at?: string | null;
    amis_push_pending_at?: string | null;
}

export interface CustomerGroupOption {
    value: string;
    label: string;
    code?: string;
}

export interface CustomerGroupCriteria {
    minRevenue12m?: number;
    minOrderCount12m?: number;
}

export interface CustomerGroup {
    _id: string;
    code: string;
    name: string;
    description?: string;
    type?: 'auto' | 'manual';
    target_type?: UserType | 'all';
    criteria?: CustomerGroupCriteria;
    color?: string;
    memberCount?: number;
    createdAt?: string;
    updatedAt?: string;
}

export type CreateCustomerGroupPayload = Omit<CustomerGroup, '_id' | 'createdAt' | 'updatedAt' | 'memberCount'>;
export type UpdateCustomerGroupPayload = Partial<CreateCustomerGroupPayload>;

function groupIdOf(value?: CustomerGroupBE | null): string | undefined {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    return value._id ?? value.id ?? value.user_group_id;
}

function groupNameOf(value?: CustomerGroupBE | null): string | undefined {
    if (!value || typeof value === 'string') return undefined;
    return value.name ?? value.group_name ?? value.code ?? value.group_code;
}

function groupCodeOf(value?: CustomerGroupBE | null): string | undefined {
    if (!value || typeof value === 'string') return undefined;
    return value.code ?? value.group_code;
}

function groupColorOf(value?: CustomerGroupBE | null): string | undefined {
    if (!value || typeof value === 'string') return undefined;
    return value.color;
}

function fromCustomerBE(c: CustomerBE): TenantCustomer {
    const groupId = groupIdOf(c.group) ?? groupIdOf(c.group_id);
    const groupName = groupNameOf(c.group) ?? groupNameOf(c.group_id);
    const groupCode = groupCodeOf(c.group) ?? groupCodeOf(c.group_id);
    const groupColor = groupColorOf(c.group) ?? groupColorOf(c.group_id);
    return {
        _id: c._id,
        tenant_customer_id: c.tenant_customer_id,
        code: c.code,
        name: c.name,
        phone: c.phone,
        email: c.email,
        birthday: c.birthday,
        gender: c.gender,
        idCard: c.idCard,
        idCardType: c.id_card_type ?? c.idCardType,
        idCardFrontUrl: c.id_card_front_url,
        idCardBackUrl: c.id_card_back_url,
        address: c.address,
        district: c.district,
        city: c.city,
        cityId: c.city_id,
        wardId: c.ward_id,
        tags: c.tags ?? [],
        totalSpent: c.total_spent ?? 0,
        loyaltyPoints: c.type === UserType.ORGANIZATION ? 0 : (c.loyalty_points ?? 0),
        loyaltyTierId: c.type === UserType.ORGANIZATION ? null : (c.loyalty_tier_id ?? null),
        lastPurchaseAt: c.last_purchase_at,
        note: c.note,
        createdAt: c.created_at ?? '',
        updatedAt: c.updated_at ?? '',
        type: c.type,
        taxCode: c.tax_code,
        taxEmail: c.tax_email,
        invoiceOrganizationName: c.invoice_organization_name,
        invoiceBuyerName: c.invoice_buyer_name,
        invoiceBuyerCccd: c.invoice_buyer_cccd,
        amisAccountObjectCode: c.amis_account_object_code,
        amisAccountObjectId: c.amis_account_object_id,
        invoiceAddress: c.invoice_address,
        groupId,
        groupName,
        groupCode,
        groupColor,
        creditLimit: c.credit_limit ?? undefined,
        b2b_channel: c.b2b_channel,
        region: c.region,
        payment_terms_days: c.payment_terms_days,
        contactName: c.contact_name,
        contactTitle: c.contact_title,
        contactPhone: c.contact_phone,
        assignedStaffId: c.assigned_staff_id,
        // TCR-1473: mảng ObjectId[] hex-string; mặc định [] khi BE cũ/chưa trả field.
        assignedSaleIds: c.assigned_sale_ids ?? [],
        partner_types: c.partner_types ?? ['customer'],
        state: c.state ?? 'active',
        // TCR-1261: trước bản fix này field bị lọc mất ở đây — HealthTab luôn
        // fallback về `?? 50` bất kể BE trả gì. Giữ nguyên undefined khi BE
        // không trả field (list endpoint không tính) thay vì tự default ở FE,
        // để HealthTab tự quyết định fallback hiển thị.
        health_score: c.health_score,
        // TCR-1437: `fromCustomerBE` là whitelist mapper — field nào không liệt kê ở đây thì bị
        // vứt lặng lẽ, UI sẽ không bao giờ thấy dù BE trả đúng. Đừng bỏ dòng này.
        // TCR-1559: thiếu dòng `amis_account_object_id` snake_case pass-through khiến
        // `deriveAmisSyncStatus` (columns.tsx) luôn nhận `undefined` dù BE trả đúng field.
        amis_account_object_id: c.amis_account_object_id,
        amis_push_blocked_reason: c.amis_push_blocked_reason,
        amis_pushed_at: c.amis_pushed_at,
        amis_push_pending_at: c.amis_push_pending_at,
    };
}

export function toCustomerBE(p: Partial<TenantCustomer>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    if (p.code !== undefined) out.code = p.code;
    if (p.name !== undefined) out.name = p.name;
    if (p.phone !== undefined) out.phone = p.phone;
    if (p.email !== undefined) out.email = p.email;
    if (p.birthday !== undefined) out.birthday = p.birthday;
    if (p.gender !== undefined) out.gender = p.gender;
    if (p.idCard !== undefined) out.idCard = p.idCard;
    if (p.idCardType !== undefined) out.idCardType = p.idCardType;
    if (p.idCardFrontUrl !== undefined) out.id_card_front_url = p.idCardFrontUrl;
    if (p.idCardBackUrl !== undefined) out.id_card_back_url = p.idCardBackUrl;
    if (p.address !== undefined) out.address = p.address;
    if (p.district !== undefined) out.district = p.district;
    if (p.city !== undefined) out.city = p.city;
    if (p.cityId !== undefined) out.city_id = p.cityId;
    if (p.wardId !== undefined) out.ward_id = p.wardId;
    if (p.tags !== undefined) out.tags = p.tags;
    if (p.note !== undefined) out.note = p.note;
    if (p.type !== undefined) out.type = p.type;
    if (p.taxCode !== undefined) out.tax_code = p.taxCode;
    if (p.taxEmail !== undefined) out.tax_email = p.taxEmail;
    if (p.invoiceOrganizationName !== undefined) out.invoice_organization_name = p.invoiceOrganizationName;
    if (p.invoiceBuyerName !== undefined) out.invoice_buyer_name = p.invoiceBuyerName;
    if (p.invoiceBuyerCccd !== undefined) out.invoice_buyer_cccd = p.invoiceBuyerCccd;
    if (p.amisAccountObjectCode !== undefined) out.amis_account_object_code = p.amisAccountObjectCode;
    if (p.amisAccountObjectId !== undefined) out.amis_account_object_id = p.amisAccountObjectId;
    if (p.invoiceAddress !== undefined) out.invoice_address = p.invoiceAddress;
    if (p.groupId !== undefined) out.group_id = p.groupId || null;
    // TCR-1042: pass-through, KHÔNG map camelCase (ADR-013). Cho phép gửi null để gỡ kênh.
    if (p.b2b_channel !== undefined) out.b2b_channel = p.b2b_channel;
    // TCR-1520: pass-through giống b2b_channel — cho phép gửi null để gỡ miền.
    if (p.region !== undefined) out.region = p.region;
    // TCR-1548: pass-through — KHÁC region/b2b_channel, chỉ gửi khi có giá trị số
    // thật (không cho gửi null — field là Number ở BE, gửi null gây CastError).
    if (p.payment_terms_days !== undefined && p.payment_terms_days !== null) {
      out.payment_terms_days = p.payment_terms_days;
    }
    // TCR-1883 v2: model FE giữ camelCase (`creditLimit`), payload BE snake_case.
    // Cả `0` (khoá công nợ) lẫn `null` (gỡ hạn mức riêng) đều là giá trị CÓ NGHĨA
    // và phải đi tới BE; chỉ `undefined` mới là "không đụng tới field này".
    if (p.creditLimit !== undefined) {
      out.credit_limit = p.creditLimit;
    }
    // TCR-466: field B2B / tổ chức — snake_case (ADR-013).
    if (p.contactName !== undefined) out.contact_name = p.contactName;
    if (p.contactTitle !== undefined) out.contact_title = p.contactTitle;
    if (p.contactPhone !== undefined) out.contact_phone = p.contactPhone;
    if (p.assignedStaffId !== undefined) out.assigned_staff_id = p.assignedStaffId;
    if (p.state !== undefined) out.state = p.state;
    if (p.partner_types !== undefined) out.partner_types = p.partner_types;
    return out;
}

/** BE list shape: `{ data: [], pagination: { total, page, limit } }` (NOT `{ data, total }`). */
interface CustomerListResponseBE {
    data: CustomerBE[];
    pagination?: { total?: number; page?: number; limit?: number };
}

export const customerService = {
    list: (params: CustomerListQuery): Promise<CustomerListResponse> =>
        api
            .get<CustomerListResponseBE>('/customers', { params: mapListQuery(params) })
            .then((r) => ({
                data: (r.data.data ?? []).map(fromCustomerBE),
                total: r.data.pagination?.total ?? 0,
            })),

    listProfiles: (params: TenantCustomerProfileListQuery): Promise<{ data: TenantCustomerProfileSummary[] }> =>
        api
            .get<{ data: TenantCustomerProfileSummary[] }>('/customers/profiles', { params })
            .then((r) => r.data),

    getProfileByTenantCustomerId: (tenantCustomerId: string): Promise<TenantCustomerProfileDetail> =>
        api
            .get<TenantCustomerProfileDetail>(`/customers/profiles/${tenantCustomerId}`)
            .then((r) => r.data),

    listGroups: (target_type?: UserType | 'all'): Promise<CustomerGroupOption[]> =>
        api
            .get<{ data?: Array<{ _id?: string; id?: string; user_group_id?: string; name?: string; code?: string }> }>('/customers/groups', {
                params: target_type ? { target_type } : undefined,
            })
            .then((r) =>
                (r.data.data ?? [])
                    .map((g) => ({
                        value: g._id ?? g.id ?? g.user_group_id ?? '',
                        label: g.name ?? g.code ?? 'Nhóm KH',
                        code: g.code,
                    }))
                    .filter((g) => g.value),
            ),

    listGroupsDetail: (target_type?: UserType | 'all'): Promise<CustomerGroup[]> =>
        api
            .get<{ data?: Array<{ _id?: string; id?: string; code?: string; name?: string; description?: string; type?: 'auto' | 'manual'; target_type?: UserType | 'all'; criteria?: CustomerGroupCriteria; color?: string; memberCount?: number }> }>('/customers/groups', {
                params: target_type ? { target_type } : undefined,
            })
            .then((r) =>
                (r.data.data ?? []).map((g) => ({
                    _id: g._id ?? g.id ?? '',
                    code: g.code ?? '',
                    name: g.name ?? '',
                    description: g.description,
                    type: g.type ?? 'manual',
                    target_type: g.target_type ?? 'all',
                    criteria: g.criteria,
                    color: g.color,
                    memberCount: g.memberCount ?? 0,
                })),
            ),

    createGroup: (body: CreateCustomerGroupPayload): Promise<CustomerGroup> => {
        return api.post<{ data?: CustomerGroup } | CustomerGroup>('/customers/groups', body).then((r) => {
            const data = (r.data as any)?.data ?? r.data;
            return data;
        });
    },

    updateGroup: (id: string, body: UpdateCustomerGroupPayload): Promise<CustomerGroup> => {
        return api.put<{ data?: CustomerGroup } | CustomerGroup>(`/customers/groups/${id}`, body).then((r) => {
            const data = (r.data as any)?.data ?? r.data;
            return data;
        });
    },

    deleteGroup: (id: string): Promise<{ ok: boolean }> =>
        api.delete<{ ok: boolean }>(`/customers/groups/${id}`).then((r) => r.data),


    getById: (id: string) =>
        api.get<CustomerBE>(`/customers/${id}`).then((r) => fromCustomerBE(r.data)),

    create: (body: CreateCustomerPayload) =>
        api
            .post<CustomerBE>('/customers', toCustomerBE(body))
            .then((r) => fromCustomerBE(r.data)),

    update: (id: string, body: UpdateCustomerPayload) =>
        api
            .put<CustomerBE>(`/customers/${id}`, toCustomerBE(body))
            .then((r) => fromCustomerBE(r.data)),

    /**
     * TCR-1473 — gán/đổi danh sách NVKD phụ trách (`assigned_sale_ids`), dùng
     * bởi `AssignSaleModal` (permission `customers.transfer`).
     *
     * (final fix wave, Fix 7) — BE `PUT /customers/:id` đã hỗ trợ đầy đủ
     * field này (`UpdateCustomerDto` đã khai báo `assigned_sale_ids`,
     * `CustomerController.update()` gate bằng `CustomerTransferGuard` +
     * `Permissions.CUSTOMERS_MANAGE`, xem `customer.controller.ts`/
     * `customer.service.ts` ở repo be-tenant) — gap 400 "property
     * assigned_sale_ids should not exist" đã đóng từ Task 10 FE follow-up.
     * Mọi lỗi 400 còn lại từ đây trở đi là validation THẬT (vd ObjectId
     * không hợp lệ trong `saleIds`), `AssignSaleModal` hiển thị nguyên văn
     * message đó cho người dùng qua `getApiError`.
     */
    updateAssignedSaleIds: (id: string, saleIds: string[]) =>
        api
            .put<CustomerBE>(`/customers/${id}`, { assigned_sale_ids: saleIds })
            .then((r) => fromCustomerBE(r.data)),

    setState: (id: string, state: CustomerLifecycleState) =>
        api
            .put<CustomerBE>(`/customers/${id}/state`, { state })
            .then((r) => fromCustomerBE(r.data)),

    remove: (id: string) => api.delete<{ ok: true }>(`/customers/${id}`).then((r) => r.data),

    listPurchases: async (id: string): Promise<CustomerPurchase[]> => {
        const res = await api.get(`/customers/${id}/purchases`);
        const body = res.data as { data?: any[]; pagination?: any };
        const orders = body.data ?? [];
        return orders.map((o: any) => ({
            _id: o._id?.toString() ?? o.order_id ?? '',
            orderCode: o.order_number ?? o.order_id?.slice(-6) ?? '',
            date: o.created_at ?? o.order_date ?? '',
            items: o.items?.length ?? o.total_items ?? 0,
            total: (() => {
                const raw = o.summary?.total ?? o.total ?? o.total_amount ?? o.grand_total ?? o.final_price ?? 0;
                return typeof raw === 'object' && raw !== null
                    ? Number(raw.$numberDecimal ?? 0)
                    : Number(raw ?? 0);
            })(),
            pointsEarned: Number(o.points_earned ?? 0),
            paymentMethod: o.payment?.method ?? o.payment_method ?? 'cash',
        }));
    },

    /**
     * TCR-1552 — lịch sử thay đổi của khách hàng (ai sửa gì, khi nào).
     * `staff_id` trả về RAW (không join tên) — cần tự tra tên qua
     * `useAllStaffUsers`, giống cách `assignedStaffId` đang làm ở trang chi
     * tiết KH (xem detail.tsx).
     */
    listLogs: (id: string, params: { page?: number; limit?: number } = {}): Promise<CustomerLogsResponse> =>
        api
            .get<{ data: CustomerLogEntryBE[]; pagination: CustomerLogsResponse['pagination'] }>(
                `/customers/${id}/logs`,
                { params },
            )
            .then((r) => r.data),

    /**
     * TCR-1050 — đơn hàng của một khách, dùng cho ô "Đơn hàng" trên form phiếu thu.
     *
     * Dùng chung endpoint với `listPurchases` nhưng giữ nguyên các field mà form phiếu thu
     * cần (`store_id`, `state`, `payment_state`) — `listPurchases` map sang `CustomerPurchase`
     * nên đã đánh rơi chúng. Lọc theo ID khách chứ không theo tên/SĐT: `GET /orders` chỉ có
     * bộ lọc văn bản, chọn nhầm đơn của khách trùng tên là sai tiền.
     *
     * BE trả ID đơn ở field `order_id` (transform `toJSON` của schema Order), không phải `_id`.
     *
     * Tìm mã đơn (`q`) và lọc trạng thái (`state` / `payment_state`) đều do SERVER làm
     * (`ListCustomerPurchasesQueryDto` ở be-tenant) — lọc ở FE sau khi BE đã cắt trang thì
     * đơn nợ cũ của khách bán công nợ không bao giờ với tới được. `fields=ref` bật projection
     * nhẹ (~200 B/đơn thay vì ~6.4 KB); nhiều giá trị gửi kiểu CSV.
     */
    listOrderRefs: async (
        id: string,
        params: {
            limit?: number;
            page?: number;
            q?: string;
            state?: string[];
            payment_state?: string[];
        } = {},
    ): Promise<{ items: CustomerOrderRef[]; total: number }> => {
        const keyword = params.q?.trim();
        const res = await api.get(`/customers/${id}/purchases`, {
            params: {
                limit: params.limit ?? 100,
                page: params.page ?? 1,
                fields: 'ref',
                ...(keyword ? { q: keyword } : {}),
                ...(params.state?.length ? { state: params.state.join(',') } : {}),
                ...(params.payment_state?.length
                    ? { payment_state: params.payment_state.join(',') }
                    : {}),
            },
        });
        const body = res.data as {
            data?: CustomerOrderRefBE[];
            pagination?: { total?: number };
        };
        const rows = body.data ?? [];
        return {
            items: rows
                .map((order) => ({
                    order_id: String(order.order_id ?? order._id ?? ''),
                    order_number: String(order.order_number ?? ''),
                    order_date: String(order.order_date ?? order.created_at ?? ''),
                    store_id: order.store_id ? String(order.store_id) : undefined,
                    state: String(order.state ?? ''),
                    payment_state: String(order.payment_state ?? ''),
                    total_amount: readOrderTotal(order),
                }))
                .filter((order) => Boolean(order.order_id)),
            total: Number(body.pagination?.total ?? rows.length) || 0,
        };
    },

    getAddresses: (id: string): Promise<{ data: CustomerAddressItem[]; success: boolean }> =>
        api.get(`/customers/${id}/addresses`).then((r) => r.data),

    addAddress: (id: string, body: Record<string, unknown>): Promise<{ data: CustomerAddressItem; success: boolean }> =>
        api.post(`/customers/${id}/addresses/add`, body).then((r) => r.data),

    updateAddress: (id: string, addressId: string, body: Record<string, unknown>): Promise<{ data: CustomerAddressItem; success: boolean }> =>
        api.post(`/customers/${id}/addresses/update/${addressId}`, body).then((r) => r.data),

    deleteAddress: (id: string, addressId: string): Promise<{ data: { message: string }; success: boolean }> =>
        api.post(`/customers/${id}/addresses/delete/${addressId}`).then((r) => r.data),

    setDefaultAddress: (id: string, addressId: string): Promise<{ data: CustomerAddressItem; success: boolean }> =>
        api.post(`/customers/${id}/addresses/set-default/${addressId}`).then((r) => r.data),

    // TCR-2384 — import nhiều địa chỉ nhận hàng: tải mẫu → xem trước → xác nhận (gửi lại đúng file).
    downloadAddressImportTemplate: (id: string): Promise<Blob> =>
        api.get<Blob>(`/customers/${id}/addresses/import-template`, { responseType: 'blob' })
            .then((r) => r.data),

    previewAddressImport: (id: string, file: File): Promise<CustomerAddressImportPreview> => {
        const form = new FormData();
        form.append('file', file);
        return api.post(`/customers/${id}/addresses/import-preview`, form, {
            headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120_000,
        }).then((r) => r.data.data);
    },

    importAddresses: (id: string, file: File): Promise<CustomerAddressImportResult> => {
        const form = new FormData();
        form.append('file', file);
        return api.post(`/customers/${id}/addresses/import`, form, {
            headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120_000,
        }).then((r) => r.data.data);
    },

    getBanks: (id: string): Promise<{ data: CustomerBankItem[]; success: boolean }> =>
        api.get(`/customers/${id}/banks`).then((r) => r.data),

    addBank: (id: string, body: Record<string, unknown>): Promise<{ data: CustomerBankItem; success: boolean }> =>
        api.post(`/customers/${id}/banks/add`, body).then((r) => r.data),

    updateBank: (id: string, bankId: string, body: Record<string, unknown>): Promise<{ data: CustomerBankItem; success: boolean }> =>
        api.post(`/customers/${id}/banks/update/${bankId}`, body).then((r) => r.data),

    deleteBank: (id: string, bankId: string): Promise<{ data: { message: string }; success: boolean }> =>
        api.post(`/customers/${id}/banks/delete/${bankId}`).then((r) => r.data),

    setDefaultBank: (id: string, bankId: string): Promise<{ data: CustomerBankItem; success: boolean }> =>
        api.post(`/customers/${id}/banks/set-default/${bankId}`).then((r) => r.data),

    // TCR-1415 — số dư trả trước (nạp/rút tại quầy). Nguồn ghi duy nhất ở BE
    // là CustomerCreditService — xem customer-credit.service.ts.
    //
    // customer-credit.controller.ts trả THẲNG giá trị của service (không bọc
    // {data, success} như các method get*/add*/... khác trong file này) —
    // phát hiện qua Chrome verify thật + curl trực tiếp API, không phải đọc
    // code suy luận (controller "trông như" nó bọc nhưng bản build/deploy
    // thật lại không — lệch giữa code review và runtime thật). r.data ở đây
    // CHÍNH LÀ payload cuối, không cần bóc thêm lớp .data nào nữa.
    getCreditSummary: (id: string): Promise<CustomerCreditSummary> =>
        api.get(`/customers/${id}/credit/summary`).then((r) => r.data),

    listCreditTransactions: (
        id: string,
        page = 1,
        limit = 20,
    ): Promise<CustomerCreditTransactionsResponse> =>
        api.get(`/customers/${id}/credit/transactions`, { params: { page, limit } }).then((r) => r.data),

    depositCredit: (
        id: string,
        body: { amount: number; method?: string; note?: string },
    ): Promise<CustomerCreditTransactionItem> =>
        api.post(`/customers/${id}/credit/deposit`, body).then((r) => r.data),

    withdrawCredit: (
        id: string,
        body: { amount: number; method?: string; note?: string },
    ): Promise<CustomerCreditTransactionItem> =>
        api.post(`/customers/${id}/credit/withdraw`, body).then((r) => r.data),
};
